import { Inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { BehaviorSubject, Observable, Subscription, merge } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  AdminUserPreferenceScope,
  PreferenceValue,
  SINGLETON_PREFERENCE_KEY
} from '../shared/api/types/admin-user-preference';
import { GqlErrorService } from '../shared/services/gql-error.service';
import { PreferencesService } from '../shared/services/preferences/preferences.service';

export type Theme = 'light' | 'dark';

/** NBO-2517: the theme is a `theme` preference on the account, `{ theme }`. */
export const THEME_PREFERENCE = 'theme';

/**
 * The local copy index.html's pre-paint script reads, so the first paint is already in the right
 * theme. Written by ThemeService only. Before NBO-2517 it was where the theme lived; it stays as the
 * copy — never remove it with the retired preference keys.
 */
export const THEME_STORAGE_KEY = 'theme';

export function themeOf(value: unknown): Theme | null {
  const theme = (value as { theme?: unknown } | null | undefined)?.theme;
  return theme === 'dark' || theme === 'light' ? theme : null;
}

export function toThemePreference(theme: Theme): PreferenceValue {
  return { theme };
}

/**
 * Whether the user may choose a theme at all. Off on production until the
 * redesign covers every screen: dark mode only works on the surfaces already
 * moved onto the Whisper token layer, and legacy screens render dark chrome
 * over light-only pages (NBO-2332's debt).
 *
 * Off means light, full stop — no stored choice applies, because a user would
 * otherwise land in the half-finished dark mode with no switch to leave it.
 * index.html's pre-paint script mirrors this gate by hostname (it runs before
 * the bundle, so it cannot read `environment`); delete both together once dark
 * mode ships.
 *
 * ── WHEN YOU TURN THIS ON, RESTORE THE OS PREFERENCE WITH IT ──
 * Following `prefers-color-scheme` for a user who has not picked a theme is the
 * right behaviour and was only removed because the switch is hidden here: dark
 * became the de facto default for every dark-laptop user, with no way out on a
 * customer build. Making the theme available to customers removes that reason.
 * Recover the code from `git log --grep "open in light mode unless the user
 * picked dark"` — it restores, in one revert:
 *   - `initializeTheme()`'s no-stored-key branch → `applyTheme(getOsPreferredTheme())`
 *     (via `applyTheme`, never `setTheme` — an OS-derived value must not be
 *     persisted as though the user had chosen it),
 *   - `getOsPreferredTheme()` / `followOsTheme()` / `onOsThemeChange` /
 *     `stopFollowingOsTheme()`, so live OS switches are honoured until the
 *     first explicit pick, which detaches the listener in `setTheme()`,
 *   - the `prefers-color-scheme` half of index.html's pre-paint script, which
 *     must keep mirroring whatever `initializeTheme()` resolves.
 */
export const THEME_SWITCH_ENABLED = new InjectionToken<boolean>('THEME_SWITCH_ENABLED', {
  providedIn: 'root',
  factory: () => !environment.production
});

@Injectable({
  providedIn: 'root'
})
export class ThemeService implements OnDestroy {
  private readonly LIGHT_THEME_CLASS = 'theme-default';
  private readonly DARK_THEME_CLASS = 'theme-alternate';

  private currentThemeSubject = new BehaviorSubject<Theme>('light');
  public currentTheme$: Observable<Theme> = this.currentThemeSubject.asObservable();

  private accountSubscription?: Subscription;

  constructor(
    private overlayContainer: OverlayContainer,
    private errorService: GqlErrorService,
    private preferences: PreferencesService,
    @Inject(THEME_SWITCH_ENABLED) readonly switchEnabled: boolean
  ) {
    // NBO-2517: the theme lives on the account; the local copy only paints the first frame.
    try {
      this.initializeTheme();
    } catch (error) {
      console.error('Error initializing ThemeService:', error);
      this.errorService.getGqlError(error);
      // Fallback to default light theme
      this.resetToDefaultTheme();
    }

    if (this.switchEnabled) {
      // The account's value, once known: the cache of the user on this machine at boot, then the
      // server's at login, then another tab's change. Merged with the load signal because a user
      // who holds no row at all announces no change — and that user is light.
      this.accountSubscription = merge(
        this.preferences.watch(THEME_PREFERENCE, SINGLETON_PREFERENCE_KEY),
        this.preferences.whenLoaded()
      ).subscribe(() => this.followAccount());
    }
  }

  /**
   * Shows the account's theme and keeps the local copy in step. Never writes the account: this is
   * the account's own value, or light for a user who never picked one. Before the user's rows are
   * known (no row in the cache, server not answered) the local copy stands.
   */
  private followAccount(): void {
    const theme =
      themeOf(this.preferences.get(THEME_PREFERENCE, SINGLETON_PREFERENCE_KEY)) ??
      (this.preferences.isLoaded() ? 'light' : null);
    if (!theme) {
      return;
    }
    if (theme !== this.currentThemeSubject.value) {
      this.applyTheme(theme);
    }
    this.writeLocalCopy(theme);
  }

  /**
   * Initialize theme from the local copy, the same key index.html already
   * painted from. Light is the default: dark applies only to a user who picked
   * it, never to one whose OS happens to be dark.
   *
   * NBO-2387 originally followed `prefers-color-scheme` so the pre-login auth
   * screens had some signal to go on. That is wrong for this app while dark
   * mode is incomplete and the switch is hidden on production builds: an
   * OS-dark customer opened the app in a half-finished dark mode with no
   * control to leave it. Opting in is the only way in.
   */
  private initializeTheme(): void {
    if (!this.switchEnabled) {
      // The stored key is left in place, not cleared: it is the user's own
      // choice and should apply again once the switch returns.
      this.resetToDefaultTheme();
      return;
    }
    try {
      const savedTheme = this.safeGetLocalStorage(THEME_STORAGE_KEY) as Theme;
      if (savedTheme === 'dark' || savedTheme === 'light') {
        // Applied, not set: the local copy is not a pick. Written to the
        // account it would be a dirty row beating what the user chose
        // elsewhere — and at boot it may even be the previous user's copy.
        this.applyTheme(savedTheme);
      } else {
        this.resetToDefaultTheme();
      }
    } catch (error) {
      console.error('Error initializing theme:', error);
      // Fallback to light theme if localStorage is not available
      this.resetToDefaultTheme();
    }
  }

  /**
   * Get the current theme
   */
  getCurrentTheme(): Theme {
    return this.currentThemeSubject.value;
  }

  /**
   * Toggle between light and dark themes
   */
  toggleTheme(): void {
    const newTheme = this.currentThemeSubject.value === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);
  }

  /**
   * Set a specific theme
   * @param theme - The theme to set ('light' or 'dark')
   */
  setTheme(theme: Theme): void {
    if (theme !== 'light' && theme !== 'dark') {
      console.warn(`Invalid theme: ${theme}. Defaulting to light theme.`);
      theme = 'light';
    }
    if (!this.switchEnabled) {
      return;
    }

    this.applyTheme(theme);
    this.writeLocalCopy(theme);
    this.preferences.set(
      THEME_PREFERENCE,
      SINGLETON_PREFERENCE_KEY,
      AdminUserPreferenceScope.Global,
      toThemePreference(theme)
    );
  }

  /**
   * Apply a theme without recording it as the user's choice. Used by the boot
   * read of the local copy and by the account follow; every user-initiated
   * path goes through setTheme().
   */
  private applyTheme(theme: Theme): void {
    this.currentThemeSubject.next(theme);
    this.applyThemeToDocument(theme);
    this.applyThemeToOverlay(theme);
  }

  /**
   * Apply theme classes to the document body AND to <app-root>.
   *
   * app-root is not optional (NBO-2331): index.html hardcodes
   * `class="theme-default"` on it, and theme.scss gives that class
   * `color-scheme: light`. Since the M3 flip every content token is a
   * `light-dark()` pair resolved by the nearest color-scheme, so leaving the
   * class pinned makes dark mode stop at the shell — chrome dark, every page
   * below it still light (seen live). index.html keeps the literal as a no-JS
   * fallback and stamps the real class pre-paint with the same resolution as
   * initializeTheme() (NBO-2387); this keeps both in sync from then on.
   * @param theme - The theme to apply
   */
  private applyThemeToDocument(theme: Theme): void {
    try {
      const themeClass = theme === 'dark' ? this.DARK_THEME_CLASS : this.LIGHT_THEME_CLASS;

      for (const element of [document.body, document.querySelector('app-root')]) {
        if (!element) {
          continue;
        }
        element.classList.remove(this.LIGHT_THEME_CLASS, this.DARK_THEME_CLASS);
        element.classList.add(themeClass);
      }
    } catch (error) {
      console.error('Error applying theme to document:', error);
    }
  }

  /**
   * Apply theme classes to the overlay container for Material components
   * @param theme - The theme to apply
   */
  applyThemeToOverlay(theme: Theme): void {
    try {
      const overlayElement = this.overlayContainer.getContainerElement();

      // Remove existing theme classes
      overlayElement.classList.remove(this.LIGHT_THEME_CLASS, this.DARK_THEME_CLASS);

      // Add new theme class
      const themeClass = theme === 'dark' ? this.DARK_THEME_CLASS : this.LIGHT_THEME_CLASS;
      overlayElement.classList.add(themeClass);
    } catch (error) {
      console.error('Error applying theme to overlay container:', error);
    }
  }

  /** The copy index.html paints from before the bundle loads. Skipped when unchanged. */
  private writeLocalCopy(theme: Theme): void {
    if (this.safeGetLocalStorage(THEME_STORAGE_KEY) !== theme) {
      this.safeSetLocalStorage(THEME_STORAGE_KEY, theme);
    }
  }

  /**
   * Check if dark theme is currently active
   */
  isDarkTheme(): boolean {
    return this.currentThemeSubject.value === 'dark';
  }

  /**
   * Check if light theme is currently active
   */
  isLightTheme(): boolean {
    return this.currentThemeSubject.value === 'light';
  }

  /**
   * Reset to default light theme
   */
  private resetToDefaultTheme(): void {
    this.currentThemeSubject.next('light');
    this.applyThemeToDocument('light');
    this.applyThemeToOverlay('light');
  }

  /**
   * Safely set localStorage with error handling and fallback
   */
  private safeSetLocalStorage(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.warn(`Failed to set localStorage key "${key}":`, error);
      // Could implement alternative storage mechanism here if needed
    }
  }

  /**
   * Safely get localStorage with error handling and fallback
   */
  private safeGetLocalStorage(key: string, fallback: string = ''): string {
    try {
      return localStorage.getItem(key) || fallback;
    } catch (error) {
      console.warn(`Failed to get localStorage key "${key}":`, error);
      return fallback;
    }
  }

  ngOnDestroy(): void {
    this.accountSubscription?.unsubscribe();
    this.currentThemeSubject.complete();
  }
}
